import { MenuItem } from "@chakra-ui/react";
import { AllocationRuleGraphTabProps } from "features/fund-family/setup/fund-structure/AllocationRuleGraphPage";
import {
  useAllocationRuleGraphAndUseRetrieve,
  useAllocationRuleGraphRetrieveSuspense,
} from "gen/endpoints/allocation-rule-graph/allocation-rule-graph";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const AllocationRuleGraphTabDetails: React.FC = () => {
  const { allocationRuleGraphId } = useRequiredStringParams();
  const { fundFamilyId, ruleGraphUseId } =
    useOutletContext<AllocationRuleGraphTabProps>();
  const { t } = useTranslation(["allocation", "common"]);

  const navigate = useNavigate();

  const isAllocationRuleGraphEditEnabled = useFeatureFlagActive(
    "allow_edit_of_allocation_rule_graphs_via_ui",
  );

  const { data: graphAndUseTableData } = useAllocationRuleGraphAndUseRetrieve(
    fundFamilyId,
    allocationRuleGraphId,
    ruleGraphUseId!,
    {
      query: {
        enabled: !!ruleGraphUseId,
      },
    },
  );

  const { data: graphData } = useAllocationRuleGraphRetrieveSuspense(
    fundFamilyId,
    allocationRuleGraphId,
  );

  const onClickEdit = () => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fundStructure({})
        .fundFamilySetupAllocationRuleGraphEdit({
          ruleGraphId: allocationRuleGraphId,
          useId: ruleGraphUseId!,
        }).$,
    );
  };

  return (
    <PageTabBody data-private={true}>
      <ContentBlockGroup>
        <ContentBlock>
          {isAllocationRuleGraphEditEnabled && (
            <ContentBlockHeader>
              <Restricted permission={PermissionTypeEnum.config}>
                <ContentBlockActions>
                  <ContentBlockMenu>
                    <MenuItem
                      isDisabled={!ruleGraphUseId}
                      onClick={onClickEdit}
                    >
                      {t("common:edit")}
                    </MenuItem>
                  </ContentBlockMenu>
                </ContentBlockActions>
              </Restricted>
            </ContentBlockHeader>
          )}
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item
                label={t("allocation:ruleGraphDetails:transactionCode")}
              >
                {graphAndUseTableData?.transaction_code_name ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("allocation:ruleGraphDetails:investment")}
              >
                {graphAndUseTableData?.investment_name ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("allocation:ruleGraphDetails:calculation")}
              >
                {graphData.calculation_name ? graphData.calculation_name : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("allocation:ruleGraphDetails:optOuts")}
              >
                {graphData.number_opt_out_investors}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
    </PageTabBody>
  );
};
